import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for the empty-chat suggested-task chips. One fetch to /api/suggested-tasks (the
// symphony-bff ListSuggestedTasks projection of the genome's `_suggested-tasks.json`). orion-web's
// CLIENT bundle must not import common-lib, so the shape lives here as a client-local contract
// matching what the route returns.

export interface SuggestedTask {
  /** The chip's visible text. Short and imperative; already localized by the genome. */
  label: string;
  /** What to put in the COMPOSER on tap. The user still presses send — never auto-send this. */
  prompt: string;
  /** Why this business specifically. Shown on hover; empty means show no tooltip. */
  why: string;
  /** Composio toolkit slug for the chip's logo, or '' when the task rides no particular app. */
  connector: string;
  /** True when `connector` is NOT connected yet — the chip must say so before it is tapped. */
  requiresConnect: boolean;
}

export type SuggestedTasksState =
  | { status: 'loading' }
  | { status: 'ready'; tasks: SuggestedTask[] }
  /** Nothing to show. Deliberately distinct from 'ready' with an empty list only in intent: both
   *  render nothing, and neither is worth an error message under a working composer. */
  | { status: 'empty' };

export async function fetchSuggestedTasks(): Promise<SuggestedTask[]> {
  const res = await apiFetch(`${API_BASE}/api/suggested-tasks`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { tasks?: Partial<SuggestedTask>[] };
  return (data.tasks ?? [])
    .map((t) => ({
      label: t.label ?? '',
      // A chip whose tap puts nothing in the composer is a dead control.
      prompt: t.prompt || t.label || '',
      why: t.why ?? '',
      connector: t.connector ?? '',
      requiresConnect: Boolean(t.requiresConnect),
    }))
    .filter((t) => t.label.length > 0);
}

/**
 * The chips for an empty chat.
 *
 * A failure here is NOT an error state: the composer under these chips is the actual feature, and a
 * red line where six optional buttons would be is worse than the buttons never appearing. So every
 * non-ready outcome — still loading, failed, none authored — resolves to a row that draws nothing.
 * The duplexer refresh matters because the genome rewrites this file on every recalc, and a user who
 * just finished onboarding should see their first chips without a reload.
 */
export function useSuggestedTasks(enabled: boolean): SuggestedTasksState {
  const query = useQuery({
    queryKey: queryKeys.suggestedTasks,
    queryFn: fetchSuggestedTasks,
    enabled,
  });

  useFileSystemDuplexer(() => {
    if (enabled) void queryClient.invalidateQueries({ queryKey: queryKeys.suggestedTasks });
  });

  if (query.data) return query.data.length > 0 ? { status: 'ready', tasks: query.data } : { status: 'empty' };
  if (query.status === 'error') return { status: 'empty' };
  return enabled ? { status: 'loading' } : { status: 'empty' };
}
